import { useEffect } from 'react';
import {
  Row, Col, Popover,
} from 'antd';

import { useSearchParams } from 'react-router-dom';
import {
  useDispatch, useSelector,
} from 'react-redux';
import {
  selectCompanyProfile,
  selectProfileDownloadPdfLoading,
  selectProfileDownloadPptLoading,
} from 'Selector/CompaniesSelector';
import { actions as CompanySliceActions } from 'Slice/CompaniesSlice';
import { actions as certificationActions } from 'Slice/CertificationSlice';
import {
  selectActivetype, selectData, selectHighlight, selectIsHighlighted, selectIsScroll,
} from 'Selector/CertificationSelector';
import classNames from 'classnames';
import { FILE_DOWNLOAD_WARNING } from 'Utils/Constants';
import { actions as helperActions } from 'Slice/HelperSlice';
import { SecondaryColor } from 'Utils/utilsStyle';
import { IsTablet } from 'Utils/UtilityFunctions';
import DownloadIcon from 'Components/common/assets/svgIcons/DownloadIcon';
import PortalAsset from '../../common/assets/svgIcons/PortalAsset';
import DigestEmail from '../../common/assets/svgIcons/DigestEmail';
import GlobalButton from '../../common/components/GlobalButton';
import { actions as companiesActions } from '../../../Slice/CompaniesSlice';
import Empty from '../../common/assets/svgIcons/Empty';
import SaveIcon from '../../common/assets/svgIcons/SaveIcon';

type Props = {
  loading: boolean,
  highlighted?: boolean,
  companyType?: string,
} & typeof defaultProps;

const defaultProps = {
  highlighted: false,
  companyType: '',
};

const DownloadProfile = function DownloadProfile(props: Props) {
  const { loading, companyType } = props;
  const dispatch = useDispatch();
  const isTab = IsTablet();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');

  const companyIdParsed = companyId ? parseInt(companyId, 10) : 0;

  const isLoadingPdf: boolean = useSelector(selectProfileDownloadPdfLoading);
  const isLoadingPpt: boolean = useSelector(selectProfileDownloadPptLoading);
  const companyProfile = useSelector(selectCompanyProfile);
  const showPlanPopup = companyProfile?.userAccountDetails?.showPlanPopup;

  const handleDownloadProfileClicked = (ispdf = 1) => {
    if (ispdf === 1) {
      dispatch(companiesActions.getPDFProfile({
        companyId: companyIdParsed,
        ispdf,
        company_name: companyProfile?.companyName,
      }));
    }
    if (ispdf === 0) {
      dispatch(companiesActions.getPPTProfile({
        companyId: companyIdParsed,
        ispdf,
        company_name: companyProfile?.companyName,
      }));
    }
    if (companyType === 'Private') {
      dispatch(helperActions.setNotification({
        message: FILE_DOWNLOAD_WARNING,
        proTip: false,
        rightPlacement: false,
        groupId: 0,
        classnameFlag: true,
        showWarning: true,
      }));
    }
  };

  const data = useSelector(selectData);

  const returnDownloadButton = (ispdf = 1, isLoading = false) => (
    <GlobalButton
      title={`Download${isLoading ? 'ing' : ''}`}
      textColor="#7E7E7E"
      outlineColor="#DADADA"
      bgColor="#fff"
      LeftSpace="12px"
      RightSpace="7px"
      RightSeparator={!isLoading}
      disabled={isLoading}
      IconRight={isLoading ? <Empty /> : <SaveIcon />}
      loading={isLoadingPdf || isLoadingPpt}
      onButtonClick={() => {
        handleDownloadProfileClicked(ispdf);
        dispatch(certificationActions.certificationAction({
          type: 'company_download_options',
          source: 'company',
          data,
        }));
      }}
    />
  );

  useEffect(() => () => { dispatch(CompanySliceActions.resetProfileDownload()); }, []);

  const onDownloadButtonClick = () => {
    dispatch(certificationActions.certificationAction({
      type: 'company_download_header',
      source: 'company',
      data,
    }));
  };

  const profileDropdown = (
    <div className="profile-dropdown">
      <h3>
        <SaveIcon />
        Download Dossier
      </h3>
      <Row gutter={6}>
        <Col span={12}>
          <div className="download-doc">
            <div className="side-by-icon">
              <DigestEmail />
              <h4>
                PDF
              </h4>
              <ul>
                <li>Company Overview</li>
                <li>Strategic Insights</li>
                <li>C-Suite Insights</li>
              </ul>
            </div>
            {returnDownloadButton(1, isLoadingPdf)}
          </div>
        </Col>
        <Col span={12}>
          <div className="download-doc">
            <div className="side-by-icon">
              <PortalAsset />
              <h4>
                Powerpoint
              </h4>
              <ul>
                <li>Company Overview</li>
                <li>Strategic Insights</li>
                <li>C-Suite Insights</li>
              </ul>
            </div>
            {returnDownloadButton(0, isLoadingPpt)}
          </div>
        </Col>
      </Row>
    </div>
  );

  const isScroll = useSelector(selectIsScroll);
  const isHighlight = useSelector(selectHighlight);
  const currentType = useSelector(selectActivetype);
  const isHighlighted = useSelector(selectIsHighlighted);

  useEffect(() => {
    if (!isHighlighted && !isScroll && currentType === 'company_download_header') {
      dispatch(certificationActions.setHighlight(true));
      dispatch(certificationActions.setIsHighlighted(false));
    }
  }, [isScroll, currentType]);

  return (

    <div>
      {loading
        ? (
          <GlobalButton
            title="Download"
            textColor="#7E7E7E"
            outlineColor="#DADADA"
            LeftSpace="12px"
            RightSpace="7px"
            innerSpace="12px 15px 12px 15px"
            PaddingSm="6px 15px 4px 15px"
            heightSm="30px"
            RightSeparator
            IconRight={<SaveIcon />}
            disabled={loading}
            loading={loading}
            buttonClassName="separatedSpace"
          />
        )
        : (
          <Popover
            placement="bottomRight"
            content={profileDropdown}
            overlayClassName="download-profile-popover"
            trigger="click"
            {...(showPlanPopup && (showPlanPopup === 3
              || showPlanPopup === 4 || showPlanPopup === 5) ? { visible: false } : {})}
            arrow={false}
          >
            <span className="download-profile-btn">
              <GlobalButton
                title="Download"
                textColor={SecondaryColor}
                outlineColor={SecondaryColor}
                RightSpace="12px"
                PaddingSm="5px 10px"
                innerSpace="8px 16px"
                heightSm="30px"
                IconLeft={isTab ? <span /> : <DownloadIcon color={SecondaryColor} />}
                onButtonClick={onDownloadButtonClick}
                buttonClassName={classNames(
                  'download-profile-blue',
                  { highlightedComponent: isHighlight && currentType === 'company_download_header' },
                  { downloadProfileBtn: isHighlight && currentType === 'company_download_header' },
                )}
              />
            </span>
          </Popover>
        )}
    </div>

  );
};

export default DownloadProfile;
DownloadProfile.defaultProps = defaultProps;
